import { useState } from "react"
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import { TOWER_GLOBAL_LEAN_COLORS } from "helpers/config/dashboards/tower/defs"
import { MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { degreesToString } from "helpers/utils/common"
import { getHeadingRelativeLabelKey } from "helpers/utils/devicePosition"
import { PlotContainer } from "widgets/styled/containers"
import { TowerRelativeAngleView } from "widgets/plots/device/devicePosition/TowerRelativeAngleView"
import PlotDialogIcon from "widgets/plots/PlotDialogIcon"
import TowerLastPositionDialog from "widgets/plots/dialogs/TowerLastPositionDialog"

import type { TowerType } from "types/towers.types"

import hvaTowerSide from "images/towers/hva-tower-side.png"
import hvaTowerFront from "images/towers/hva-tower-front.png"
import mastTowerSide from "images/towers/mast-tower-side.png"
import mastTowerFront from "images/towers/mast-tower-front.png"
import poleTowerSide from "images/towers/pole-tower-side.png"
import poleTowerFront from "images/towers/pole-tower-front.png"
import genericTowerSide from "images/towers/generic-tower-side.png"
import genericTowerFront from "images/towers/generic-tower-front.png"
import TowerPositionFromAbove from "./TowerPositionFromAbove"

const TOWER_LEAN_IMAGES: Partial<Record<TowerType, { side: string; front: string }>> = {
  steel_lattice_guyed_a_frame: { side: hvaTowerSide, front: hvaTowerFront },
  steel_lattice_guyed_mast: { side: mastTowerSide, front: mastTowerFront },
  pole: { side: poleTowerSide, front: poleTowerFront },
}

const DEFAULT_LEAN_IMAGES = { side: genericTowerSide, front: genericTowerFront }

type SectionStatus = "loading" | "error" | "empty" | "ready"

const getStatus = (
  isLoading: boolean,
  isError: boolean,
  hasData: boolean,
): SectionStatus => {
  if (isError) return "error"
  if (isLoading && !hasData) return "loading"
  if (!hasData) return "empty"
  return "ready"
}

interface TowerPositionIllustrationProps {
  frontTilt: number
  rightTilt: number
  magnitude: number
  heading: number
  time: number
  towerType?: TowerType
  isLoading?: boolean
  isError?: boolean
}

const TowerPositionIllustration = ({
  frontTilt,
  rightTilt,
  magnitude,
  heading,
  time,
  towerType,
  isLoading = false,
  isError = false,
}: TowerPositionIllustrationProps) => {
  const leanImages = (towerType && TOWER_LEAN_IMAGES[towerType]) ?? DEFAULT_LEAN_IMAGES
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const baseRow = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)

  const rowHeight = Math.min(baseRow, 92)
  const { t } = useTranslation()
  const [infoOpen, setInfoOpen] = useState(false)

  const cardHeight = `${rowHeight * 2}px`

  const cardSx = {
    backgroundColor: theme.palette.background.default,
    border: "1px solid",
    borderColor: theme.palette.neutral[400],
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
  } as const

  const statCardSx = {
    ...cardSx,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: 2,
    py: 1.5,
  } as const

  const valueCardSx = {
    ...cardSx,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: 2,
    py: 1.5,
  } as const

  const valueSx = { fontSize: "1.6rem", fontWeight: 700 }

  const hasData = Number.isFinite(time)
  const status = getStatus(isLoading, isError, hasData)

  const hasHeaderTime = hasData

  const stateMessage = (status: SectionStatus) => (
    <Typography
      variant="caption"
      sx={{ color: status === "error" ? "error.main" : "text.secondary" }}
    >
      {t(status === "error" ? "generic.ERROR" : "table.NO_DATA")}
    </Typography>
  )

  const leanPlacementSx = {
    gridRow: { sm: "1", lg: "1 / span 2" },
    height: { xs: `${rowHeight * 3.5}px`, sm: "100%", lg: "100%" },
  } as const

  const positionPlacementSx = {
    gridColumn: { xs: "1", sm: "1 / span 2", lg: "auto" },
    gridRow: { sm: "2", lg: "1 / span 2" },
    height: { xs: `${rowHeight * 4.5}px`, sm: "100%", lg: "100%" },
  } as const

  const statPlacementSx = {
    gridRow: { sm: "3", lg: "auto" },
    height: { xs: `${rowHeight * 1.6}px`, sm: "100%", lg: "100%" },
  } as const

  const leanCardSx = { ...cardSx, ...leanPlacementSx } as const

  // A loading section is replaced by a single skeleton filling its whole cell.
  const cellSkeleton = (placementSx: object) => (
    <Skeleton
      variant="rounded"
      sx={{ ...placementSx, width: "100%", borderRadius: 3, transform: "none" }}
    />
  )

  const renderLean = (src: string, value: number, viewKey: "first" | "second") =>
    status === "ready" ? (
      <TowerRelativeAngleView
        src={src}
        rowHeight={rowHeight}
        value={value}
        viewKey={viewKey}
        valueSx={valueSx}
        labelVariant="lean"
      />
    ) : (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {stateMessage(status)}
      </Box>
    )

  return (
    <PlotContainer
      type=""
      disableGutters
      maxWidth="xl"
      sx={{ marginTop: "1rem", marginLeft: 0 }}
    >
      {/* Header */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        mb="0.5rem"
      >
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography variant="h5">{t("plot_titles.LAST_POSITIONS") + ":"}</Typography>
          {hasHeaderTime && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", fontWeight: 400 }}
            >
              {DateTime.fromMillis(time).toFormat("yyyy-MM-dd HH:mm:ss")}
            </Typography>
          )}
          {isLoading && hasHeaderTime && <CircularProgress size={20} />}
        </Stack>
        {status === "ready" && <PlotDialogIcon onClick={() => setInfoOpen(true)} />}
      </Stack>

      <TowerLastPositionDialog open={infoOpen} onClose={() => setInfoOpen(false)} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "2fr 2fr 3fr 1.3fr",
          },
          gridTemplateRows: {
            sm: `calc(${cardHeight} * 2) calc(${cardHeight} * 2) calc(${cardHeight} * 0.75)`,
            lg: `calc(${cardHeight} * 1.25) calc(${cardHeight} * 1.25)`,
          },
          gap: 1,
        }}
      >
        {/* Front lean — spans both rows at lg */}
        {status === "loading" ? (
          cellSkeleton(leanPlacementSx)
        ) : (
          <Box sx={leanCardSx}>{renderLean(leanImages.side, frontTilt, "first")}</Box>
        )}

        {/* Right lean — spans both rows at lg */}
        {status === "loading" ? (
          cellSkeleton(leanPlacementSx)
        ) : (
          <Box sx={leanCardSx}>{renderLean(leanImages.front, rightTilt, "second")}</Box>
        )}

        {/* Position seen from above — spans both rows at lg */}
        {status === "loading" ? (
          cellSkeleton(positionPlacementSx)
        ) : (
          <Box
            sx={{
              ...statCardSx,
              justifyContent: "flex-start",
              ...positionPlacementSx,
            }}
          >
            <Typography variant="overlineDense">{t("plot_titles.POSITION")}</Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontStyle: "italic", mb: 0.5 }}
            >
              {t("plots_descriptions.DEVICE_POSITION.TOWER_FROM_ABOVE")}
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {status === "ready" ? (
                <TowerPositionFromAbove magnitude={magnitude} heading={heading} />
              ) : (
                stateMessage(status)
              )}
            </Box>
          </Box>
        )}

        {/* Magnitude stat card */}
        {status === "loading" ? (
          cellSkeleton(statPlacementSx)
        ) : (
          <Box sx={{ ...valueCardSx, ...statPlacementSx }}>
            <Typography variant="overlineDense" sx={{ mb: 0.5 }}>
              {t("plot_titles.MAGNITUDE")}
            </Typography>
            {status === "ready" ? (
              <Typography
                sx={{
                  ...valueSx,
                  color: TOWER_GLOBAL_LEAN_COLORS.magnitude,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {degreesToString(magnitude)}
              </Typography>
            ) : (
              stateMessage(status)
            )}
          </Box>
        )}

        {/* Heading stat card */}
        {status === "loading" ? (
          cellSkeleton(statPlacementSx)
        ) : (
          <Box sx={{ ...valueCardSx, ...statPlacementSx }}>
            <Typography variant="overlineDense" sx={{ mb: 0.5 }}>
              {t("plot_titles.HEADING")}
            </Typography>
            {status === "ready" ? (
              <>
                <Typography
                  sx={{
                    ...valueSx,
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {`${Math.round(heading)}°`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {t(getHeadingRelativeLabelKey(heading))}
                </Typography>
              </>
            ) : (
              stateMessage(status)
            )}
          </Box>
        )}
      </Box>
    </PlotContainer>
  )
}

export default TowerPositionIllustration
